import { HonorsClubControllerService } from '@/__generated__/FrontApi';
import Button from '@/components/Button';
import ErrorMessage from '@/components/Form/ErrorMessage';
import FormGroup from '@/components/Form/FormGroup';
import LabelText from '@/components/Form/LabelText';
import InputCheckbox from '@/components/Input/InputCheckbox';
import InputNumber from '@/components/Input/InputNumber';
import InputText from '@/components/Input/InputText';
import InputTextarea from '@/components/Input/InputTextarea';
import { emailRegex, mobileRegex } from '@/helpers/ValidationHelper';
import { usePopupStore } from '@/stores/PopupStore';
import React, { FC, useCallback } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

interface HospitalInquiryData {
  hospitalName: string;
  userMobile: string;
  userEmail: string;
  inquiry: string;
  agreeCheck: boolean;
}

interface HospitalInquiryCardProps {
  onComplete: (isComplete: boolean) => void;
}

const HospitalInquiryCard: FC<HospitalInquiryCardProps> = ({ onComplete }) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<HospitalInquiryData>({
    defaultValues: {
      hospitalName: ``,
      userMobile: ``,
      userEmail: ``,
      inquiry: ``,
      agreeCheck: false,
    },
  });
  const popupStore = usePopupStore();

  const onSubmit: SubmitHandler<HospitalInquiryData> = useCallback(
    async ({ hospitalName, userMobile, userEmail, inquiry, agreeCheck }) => {
      try {
        const {
          resultCode,
          resultMessage,
        } = await HonorsClubControllerService.insertHonorsQnaUsingPost({
          name: hospitalName,
          mobile: userMobile,
          email: userEmail,
          isAgreeYn: agreeCheck ? `Y` : `N`,
          type: `hospital`,
          inquiry: inquiry,
        });
        if (resultCode === 'success') {
          reset();
          onComplete(true);
        } else {
          onComplete(false);
        }
        popupStore.show(resultMessage);
      } catch {
        popupStore.show(`문의 등록에 실패했습니다. 다시 시도해주세요.`);
      }
    },
    [popupStore, reset, onComplete],
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="form-container">
        <FormGroup className="full">
          <LabelText require>병원명</LabelText>
          <InputText
            placeholder="병원명 입력"
            ref={register({
              required: {
                value: true,
                message: `병원명을 입력해주세요.`,
              },
              maxLength: {
                value: 20,
                message: `20자 이하로 입력해 주세요.`,
              },
            })}
            name="hospitalName"
          />
          {errors.hospitalName && (
            <ErrorMessage>{errors.hospitalName.message}</ErrorMessage>
          )}
        </FormGroup>
        <div className="clear" />
        <FormGroup className="full">
          <LabelText require>연락처</LabelText>
          <Controller
            control={control}
            name="userMobile"
            rules={{
              required: {
                value: true,
                message: `연락처를 입력해주세요.`,
              },
              pattern: {
                value: mobileRegex,
                message: `올바른 연락처 형식이 아닙니다.`,
              },
            }}
            as={
              <InputNumber
                format="###########"
                placeholder="연락처 입력 ( &lsquo;-&rsquo; 제외 )"
              />
            }
          />
          {errors.userMobile && (
            <ErrorMessage>{errors.userMobile.message}</ErrorMessage>
          )}
        </FormGroup>
        <FormGroup className="full">
          <LabelText require>이메일</LabelText>
          <InputText
            name="userEmail"
            ref={register({
              required: {
                value: true,
                message: `이메일을 입력해주세요.`,
              },
              pattern: {
                value: emailRegex,
                message: `올바른 이메일 형식이 아닙니다.`,
              },
              maxLength: {
                value: 80,
                message: `80자 이내로 입력해주세요.`,
              },
            })}
            placeholder="이메일 주소 입력"
          />
          {errors.userEmail && (
            <ErrorMessage>{errors.userEmail.message}</ErrorMessage>
          )}
        </FormGroup>
        <FormGroup className="full">
          <LabelText>문의사항</LabelText>
          <InputTextarea
            name="inquiry"
            placeholder="문의사항을 입력해주세요."
            ref={register({
              maxLength: {
                value: 30000,
                message: `30000자 이내로 입력해주세요.`,
              },
            })}
          />
        </FormGroup>
        <FormGroup className="full">
          <InputCheckbox
            className="chk-label"
            ref={register({
              required: {
                value: true,
                message: `약관 동의에 체크해주세요.`,
              },
            })}
            name="agreeCheck"
            label="예, 저는 맞춤안내를 받고싶습니다. 그리고 안내를 원치 않을 시, 언제든지 수신거부 할 수 있음을 잘 알고 있습니다."
          />
          {errors.agreeCheck && (
            <ErrorMessage>{errors.agreeCheck.message}</ErrorMessage>
          )}
        </FormGroup>
      </div>
      <div className="btn-wrap">
        <Button className="btn" type="submit">
          문의하기
        </Button>
      </div>
    </form>
  );
};
export default HospitalInquiryCard;
